import { GetStateByStateKeyResponse } from '@iwoca/lapi-client/edge';
import { produce } from 'immer';
import { v4 as uuidv4 } from 'uuid';

import {
    TFormValues,
    TPerson,
    TSerialisedAddress,
    TSuggestion
} from './MSHForm.types';

const removedUndefinedValues = (object: { [k: string]: any }) =>
    Object.fromEntries(
        Object.entries(object).filter(([, val]) => {
            return val !== undefined;
        })
    );

export const convertDeserialisedPersonToFormValues = (
    deserialisedPerson: TPerson
) => {
    const { uid, title, firstName, lastName, dateOfBirth, roles, addresses } =
        deserialisedPerson;
    const { day, month, year } = dateOfBirth!;
    const address = addresses![0];
    const sanitisedAddress = removedUndefinedValues(address);

    const mappedFormValues = {
        uid: uid,
        title: title,
        firstName: firstName,
        lastName: lastName,
        day: day,
        month: month,
        year: year,
        director: roles?.includes('director'),
        shareholder: roles?.includes('shareholder'),
        guarantor: roles?.includes('guarantor'),
        address: sanitisedAddress
    };

    return removedUndefinedValues(mappedFormValues);
};

export const convertSuggestionToFormValues = (
    suggestion: TSuggestion
): Partial<TFormValues> => {
    return removedUndefinedValues({
        title: suggestion.title?.toLowerCase(),
        firstName: suggestion.middle_names
            ? `${suggestion.first_name} ${suggestion.middle_names}`
            : suggestion.first_name,
        lastName: suggestion.last_name,
        month: suggestion.month_of_birth
            ? String(suggestion.month_of_birth).padStart(2, '0')
            : undefined,
        year: suggestion.year_of_birth
            ? String(suggestion.year_of_birth)
            : undefined,
        director: suggestion.roles.includes('director'),
        shareholder: suggestion.roles.includes('shareholder'),
        guarantor: false,
        address: { postcode: '' }
    });
};

export const convertSuggestionToPerson = (
    suggestion: TSuggestion
): TPerson => ({
    uid: `${suggestion.first_name}|${suggestion.last_name}`,
    firstName: suggestion.middle_names
        ? `${suggestion.first_name} ${suggestion.middle_names}`
        : suggestion.first_name,
    lastName: suggestion.last_name,
    isDirector: suggestion.roles.includes('director'),
    isShareholder: suggestion.roles.includes('shareholder'),
    isApplicant: false,
    isGuarantor: false,
    email: '',
    emailUID: '',
    marketing: false,
    roles: suggestion.roles,
    dateOfBirth: {
        month: suggestion.month_of_birth
            ? String(suggestion.month_of_birth).padStart(2, '0')
            : undefined,
        year: suggestion.year_of_birth
            ? String(suggestion.year_of_birth)
            : undefined
    }
});

const normalize = (value: string | undefined | null): string =>
    (value ?? '').toLowerCase().trim();

const lastNameMatches = (person: TPerson, suggestion: TSuggestion): boolean =>
    normalize(person.lastName) === normalize(suggestion.last_name);

const firstNameMatches = (
    person: TPerson,
    suggestion: TSuggestion
): boolean => {
    const saved = normalize(person.firstName);
    const first = normalize(suggestion.first_name);

    if (!saved || !first) return false;
    if (saved === first) return true;

    if (suggestion.middle_names) {
        const firstWithMiddle = `${first} ${normalize(suggestion.middle_names)}`;
        if (saved === firstWithMiddle) return true;
    }

    return false;
};

const dateOfBirthMatches = (
    person: TPerson,
    suggestion: TSuggestion
): boolean => {
    const savedMonth = person.dateOfBirth?.month
        ? Number(person.dateOfBirth.month)
        : null;
    const savedYear = person.dateOfBirth?.year
        ? Number(person.dateOfBirth.year)
        : null;
    const suggMonth = suggestion.month_of_birth ?? null;
    const suggYear = suggestion.year_of_birth ?? null;

    if ((!savedMonth && !savedYear) || (!suggMonth && !suggYear)) return true;

    const yearConflicts = savedYear && suggYear && savedYear !== suggYear;
    const monthConflicts = savedMonth && suggMonth && savedMonth !== suggMonth;

    return !yearConflicts && !monthConflicts;
};

const isSamePerson = (person: TPerson, suggestion: TSuggestion): boolean =>
    lastNameMatches(person, suggestion) &&
    firstNameMatches(person, suggestion) &&
    dateOfBirthMatches(person, suggestion);

export const filterOutAlreadySavedSuggestions = (
    suggestions: TSuggestion[],
    savedPeople: TPerson[]
): TSuggestion[] =>
    suggestions.filter(
        (suggestion) =>
            !savedPeople.some((person) => isSamePerson(person, suggestion))
    );

export const hasMissingSuggestionShareholderRole = (
    person: TPerson,
    suggestions: TSuggestion[]
): boolean => {
    if (person.isShareholder) return false;

    return suggestions.some(
        (suggestion) =>
            suggestion.roles.includes('shareholder') &&
            isSamePerson(person, suggestion)
    );
};

export const createCustomerStateWithNewOrUpdatedPerson = (
    values: TFormValues,
    customerState: GetStateByStateKeyResponse
) => {
    return produce(customerState, (draft) => {
        let draftPerson = draft.data.application!.people!.find(
            (existingPerson) => existingPerson.uid === values.uid
        );
        if (draftPerson === undefined) {
            draftPerson = {
                uid: values.uid || uuidv4(),
                title: values.title,
                first_name: values.firstName,
                last_name: values.lastName,
                date_of_birth: `${values.year}-${values.month}-${values.day}`,
                roles: [],
                residential_addresses: []
            };
            draft.data.application!.people!.push(draftPerson);
        }

        for (const { role, shouldBeApplied } of [
            { role: 'director', shouldBeApplied: values.director },
            { role: 'shareholder', shouldBeApplied: values.shareholder },
            { role: 'guarantor', shouldBeApplied: values.guarantor }
        ] as const) {
            if (shouldBeApplied && !draftPerson.roles?.includes(role)) {
                draftPerson.roles = [...(draftPerson.roles ?? []), role];
            } else if (!shouldBeApplied && draftPerson.roles?.includes(role)) {
                draftPerson.roles = draftPerson.roles!.filter(
                    (r) => r !== role
                );
            }
        }

        const serialisedAddress: TSerialisedAddress = {
            ...(draftPerson.residential_addresses?.[0] ?? {}),
            uid: values.address.uid ? values.address.uid : uuidv4()
        };

        if (values.address.house_number)
            serialisedAddress.house_number = values.address.house_number;
        if (values.address.house_name)
            serialisedAddress.house_name = values.address.house_name;
        if (values.address.street_line_1)
            serialisedAddress.street_line_1 = values.address.street_line_1;
        if (values.address.street_line_2)
            serialisedAddress.street_line_2 = values.address.street_line_2;
        if (values.address.town) serialisedAddress.town = values.address.town;
        if (values.address.postcode)
            serialisedAddress.postcode = values.address.postcode;
        if (values.address.equifax_token)
            serialisedAddress.equifax_token = values.address.equifax_token;

        draftPerson.residential_addresses = [serialisedAddress];
    });
};
